'use client';
import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, Shield } from 'lucide-react';

type Likelihood = 'Low' | 'Low-Med' | 'Medium' | 'High';
type Impact = 'Low' | 'Medium' | 'High';
type Residual = 'Low' | 'Medium' | 'High';

interface RiskItem {
  id: string;
  risk: string;
  likelihood: Likelihood;
  impact: Impact;
  mitigant: string;
  residual: Residual;
}

const RISKS: RiskItem[] = [
  {
    id: 'risk-wc',
    risk: 'Under-capitalisation / Working Capital Squeeze',
    likelihood: 'Medium',
    impact: 'High',
    mitigant:
      'Raise buffer equity or higher CGTMSE WC limit; strict cash discipline; 2.2-month RM buffer already modelled',
    residual: 'Medium',
  },
  {
    id: 'risk-feedstock',
    risk: 'Feedstock Price Spike / Shortage',
    likelihood: 'Medium',
    impact: 'High',
    mitigant:
      'Multi-feedstock strategy (parali + bagasse + others); annual farmer/FPO contracts; baler for parali',
    residual: 'Medium',
  },
  {
    id: 'risk-offtake',
    risk: 'Offtake Delay / Volume Shortfall',
    likelihood: 'Medium',
    impact: 'High',
    mitigant:
      'Secure written vendor registration + LOI from NTPC/Rosa/other TPPs before full drawdown',
    residual: 'Medium',
  },
  {
    id: 'risk-tech',
    risk: 'Technology / Plant Uptime Issues',
    likelihood: 'Low-Med',
    impact: 'Medium',
    mitigant: 'Choose proven Indian OEM with AMC for 5 TPH line; keep critical spares inventory',
    residual: 'Low',
  },
  {
    id: 'risk-interest',
    risk: 'Interest Rate / Debt Service Stress',
    likelihood: 'Low-Med',
    impact: 'Medium',
    mitigant: 'CGTMSE + fixed rate if possible; strong DSCR headroom at 5 TPH scale',
    residual: 'Low',
  },
  {
    id: 'risk-regulatory',
    risk: 'Regulatory / Lease / Environmental',
    likelihood: 'Low',
    impact: 'High',
    mitigant: 'Clean 30-yr registered lease; early environmental & consent to establish clearances',
    residual: 'Low',
  },
  {
    id: 'risk-governance',
    risk: 'Partnership / Governance Friction',
    likelihood: 'Medium',
    impact: 'Medium',
    mitigant: 'Clear SHA; convert to Pvt Ltd early; defined roles & decision rights',
    residual: 'Medium',
  },
  {
    id: 'risk-power',
    risk: 'Power Cost Inflation (No Solar)',
    likelihood: 'Medium',
    impact: 'Medium',
    mitigant: 'Monitor grid tariff; evaluate solar addition as Phase-2 once cash flows stabilise',
    residual: 'Medium',
  },
];

function LikelihoodBadge({ value }: { value: Likelihood }) {
  if (value === 'High') return <span className="badge-danger">{value}</span>;
  if (value === 'Medium') return <span className="badge-warning">{value}</span>;
  if (value === 'Low-Med') return <span className="badge-warning">{value}</span>;
  return <span className="badge-success">{value}</span>;
}

function ImpactBadge({ value }: { value: Impact }) {
  if (value === 'High') return <span className="badge-danger">{value}</span>;
  if (value === 'Medium') return <span className="badge-warning">{value}</span>;
  return <span className="badge-success">{value}</span>;
}

function ResidualBadge({ value }: { value: Residual }) {
  if (value === 'High') return <span className="badge-danger">{value}</span>;
  if (value === 'Medium') return <span className="badge-warning">{value}</span>;
  return <span className="badge-success">{value}</span>;
}

export default function RiskMatrix() {
  const [expandedRisk, setExpandedRisk] = useState<string | null>(null);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold text-foreground">
          Risk Matrix & Investment Committee Recommendation
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          5 TPH Biomass Pellet Plant — Shiva Green Energy — Aug 2026
        </p>
      </div>

      {/* Risk heat summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: 'High Likelihood',
            count: RISKS.filter((r) => r.likelihood === 'High').length,
            variant: 'danger',
          },
          {
            label: 'Medium Likelihood',
            count: RISKS.filter((r) => r.likelihood === 'Medium' || r.likelihood === 'Low-Med')
              .length,
            variant: 'warning',
          },
          {
            label: 'High Impact',
            count: RISKS.filter((r) => r.impact === 'High').length,
            variant: 'danger',
          },
          {
            label: 'Low Residual',
            count: RISKS.filter((r) => r.residual === 'Low').length,
            variant: 'success',
          },
        ].map((item) => (
          <div
            key={`risk-summary-${item.label}`}
            className={`p-3 rounded-xl border text-center
              ${item.variant === 'danger' ? 'bg-danger-bg border-red-200' : ''}
              ${item.variant === 'warning' ? 'bg-warning-bg border-amber-200' : ''}
              ${item.variant === 'success' ? 'bg-success-bg border-green-200' : ''}
            `}
          >
            <p
              className={`font-mono text-2xl font-bold
              ${item.variant === 'danger' ? 'text-danger' : ''}
              ${item.variant === 'warning' ? 'text-warning' : ''}
              ${item.variant === 'success' ? 'text-primary' : ''}
            `}
            >
              {item.count}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{item.label}</p>
          </div>
        ))}
      </div>

      {/* Risk matrix table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-muted border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Risk Register</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Click any row to expand mitigant details
          </p>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs" style={{ minWidth: '700px' }}>
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left py-2.5 px-4 font-semibold text-muted-foreground min-w-[200px]">
                  Risk
                </th>
                <th className="text-center py-2.5 px-3 font-semibold text-muted-foreground whitespace-nowrap">
                  Likelihood
                </th>
                <th className="text-center py-2.5 px-3 font-semibold text-muted-foreground whitespace-nowrap">
                  Impact
                </th>
                <th className="text-center py-2.5 px-3 font-semibold text-muted-foreground whitespace-nowrap">
                  Residual
                </th>
                <th className="text-left py-2.5 px-4 font-semibold text-muted-foreground">
                  Mitigant
                </th>
              </tr>
            </thead>
            <tbody>
              {RISKS.map((risk) => (
                <React.Fragment key={`risk-frag-${risk.id}`}>
                  <tr
                    className="border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => setExpandedRisk(expandedRisk === risk.id ? null : risk.id)}
                  >
                    <td className="py-3 px-4 font-medium text-foreground">
                      <div className="flex items-start gap-2">
                        <AlertTriangle size={13} className="text-warning mt-0.5 flex-shrink-0" />
                        <span>{risk.risk}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <LikelihoodBadge value={risk.likelihood} />
                    </td>
                    <td className="py-3 px-3 text-center">
                      <ImpactBadge value={risk.impact} />
                    </td>
                    <td className="py-3 px-3 text-center">
                      <ResidualBadge value={risk.residual} />
                    </td>
                    <td className="py-3 px-4 text-muted-foreground leading-relaxed">
                      {expandedRisk === risk.id ? (
                        <span className="text-foreground">{risk.mitigant}</span>
                      ) : (
                        <span className="truncate block max-w-xs">
                          {risk.mitigant.slice(0, 60)}…
                        </span>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bankability Assessment */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="p-4 bg-success-bg border border-green-200 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={16} className="text-primary" />
            <h3 className="text-sm font-semibold text-primary">Bank Financeable (CGTMSE)</h3>
          </div>
          <p className="text-xs text-foreground/80 leading-relaxed">
            <span className="font-semibold">YES — with conditions.</span> Requires proper DPR,
            offtake evidence, promoter KYC, Udyam registration, company structure (Pvt Ltd), and
            DSCR covenant ≥ 1.50x.
          </p>
        </div>
        <div className="p-4 bg-info-bg border border-blue-200 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={16} className="text-accent" />
            <h3 className="text-sm font-semibold text-accent">Strategic / Financial Investor</h3>
          </div>
          <p className="text-xs text-foreground/80 leading-relaxed">
            <span className="font-semibold">MORE ATTRACTIVE at 5 TPH</span> than 2 TPH for both
            strategic (offtake/solar) and financial investors seeking scale. Better unit economics
            and DSCR headroom.
          </p>
        </div>
      </div>

      {/* Final Recommendation */}
      <div className="bg-card border-2 border-primary/30 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-success-bg flex items-center justify-center flex-shrink-0">
            <CheckCircle2 size={20} className="text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h3 className="text-base font-semibold text-foreground">Final Recommendation</h3>
              <span className="badge-success text-sm px-3 py-1">GO WITH CONDITIONS</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Overall Project Data Confidence Score:{' '}
              <span className="font-semibold text-foreground">7.0 / 10</span> — rises to{' '}
              <span className="font-semibold text-primary">8.5+/10</span> once offtake LOI, final
              quotes, company incorporation and lease are complete.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                {
                  num: '1',
                  text: 'Convert partnership firm into a Private Limited Company with clear shareholding.',
                },
                {
                  num: '2',
                  text: 'Execute clean, registered 30-year lease deed for the 10–12 acre parcel.',
                },
                {
                  num: '3',
                  text: 'Obtain at least one written vendor registration/LOI from NTPC, Rosa or equivalent TPP.',
                },
                {
                  num: '4',
                  text: 'Finalise machinery quotation from proven OEM for 5 TPH line within ₹3.0–3.5 Cr envelope.',
                },
                {
                  num: '5',
                  text: 'Secure CGTMSE-backed term loan + WC sanction with DSCR covenant ≥ 1.50x.',
                },
                { num: '6', text: 'Maintain minimum ₹1.4 Cr working capital buffer at COD.' },
                {
                  num: '7',
                  text: 'Complete environmental / consent-to-establish clearances before civil works.',
                },
                {
                  num: '8',
                  text: '(Optional) Explore Phase-2 500–1000 kW solar once Year-1 cash flows are proven.',
                },
              ].map((c) => (
                <div
                  key={`final-cond-${c.num}`}
                  className="flex items-start gap-2 p-2 rounded-lg bg-muted/50"
                >
                  <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {c.num}
                  </span>
                  <p className="text-xs text-foreground/80 leading-relaxed">{c.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sources note */}
      <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
        <Info size={13} className="text-muted-foreground mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground">
          Scaling assumptions: Capacity 2.5× (12,000 → 30,000 MT) · Machinery ~2.56× · Civil ~1.9× ·
          Electrical ~2.25× · WC ~3.5× · Fixed salaries ~1.75× vs 2 TPH baseline · Variable
          costs/tonne slightly lower due to scale economies · Total Project Cost ₹6.25 Cr vs ₹2.45
          Cr for 2 TPH (lean configuration, no solar)
        </p>
      </div>
    </div>
  );
}
